/**
 * Dive slate.
 *
 * Build a GoPro Labs QR code from the water you are actually getting into,
 * read why every token is there, then put six of them on a page you can take
 * underwater.
 */

import { useEffect, useMemo, useState } from "react";

import { ChipGroup } from "./components/ChipGroup";
import { Diagnostics } from "./components/Diagnostics";
import { IconPickerDialog } from "./components/IconPickerDialog";
import { OfflineBadge } from "./components/OfflineBadge";
import { PayloadPanel } from "./components/PayloadPanel";
import { PresetIconGlyph } from "./components/PresetIconGlyph";
import { QrCode } from "./components/QrCode";
import { Section } from "./components/Section";
import { Shelf } from "./components/Shelf";
import { FeedbackDialog } from "./components/FeedbackDialog";
import { SlateDialog } from "./components/SlateDialog";
import { Spectrum } from "./components/Spectrum";
import { TextField } from "./components/TextField";
import { ThemeToggle } from "./components/ThemeToggle";
import { WhyList } from "./components/WhyList";
import { UpdateBanner } from "./components/UpdateBanner";
import { WaterColumn } from "./components/WaterColumn";
import { useShelf } from "./hooks/useShelf";
import { getPendingUpdate, onUpdateReady } from "./lib/serviceWorker";
import { CAMERA_LABELS, SELECTABLE_CAMERAS, supports, type Camera } from "./lib/cameras";
import { adviseOnConditions, compileConditions } from "./lib/compiler";
import {
  ASPECTS_BY_RESOLUTION,
  DEFAULT_CONDITIONS,
  DEFAULT_PRESET_ICON,
  EV_COMP_LABELS,
  EV_COMP_VALUES,
  FRAME_RATES,
  LIGHT_LABELS,
  LIGHT_SOURCES,
  PERSISTENCE_MODE_LABELS,
  PERSISTENCE_MODES,
  RESOLUTIONS,
  SUBJECT_LABELS,
  SUBJECTS,
  VISIBILITIES,
  VISIBILITY_LABELS,
  WATER_LABELS,
  WATER_TYPES,
  WORKFLOW_LABELS,
  WORKFLOWS,
  type AspectRatio,
  type DiveConditions,
  type FrameRate,
  type PersistenceMode,
  type Resolution,
} from "./lib/conditions";
import { cleanMacroName, MAX_MACRO_NAME, MAX_PRESET_NAME, recommend, RESOLUTION_TOKENS } from "./lib/engine";

const options = <T extends string | number>(values: readonly T[], labels: Record<T, string>) =>
  values.map((value) => ({ value, label: labels[value] }));

const formatKey = (resolution: Resolution, aspect: AspectRatio) => `${resolution}|${aspect}`;

// Resolution and aspect ratio read as one decision — "what format is this
// shot" — not two, now that every resolution offers the same three aspects.
// Filtered to what `camera` actually supports: a chip for a combination this
// body cannot shoot is a chip that only exists to be picked and then warned
// about, so it is left off rather than offered and dropped later.
const formatOptionsFor = (camera: Camera) =>
  RESOLUTIONS.flatMap((resolution) =>
    ASPECTS_BY_RESOLUTION[resolution].flatMap((aspect) => {
      const token = RESOLUTION_TOKENS[resolution][aspect];
      if (!token || (token.feature && !supports(camera, token.feature))) return [];
      return [{ value: formatKey(resolution, aspect), label: token.label, resolution, aspect }];
    }),
  );

export default function App() {
  const [conditions, setConditions] = useState<DiveConditions>(DEFAULT_CONDITIONS);
  const [exporting, setExporting] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [update, setUpdate] = useState<(() => void) | null>(null);
  const shelf = useShelf();

  useEffect(() => onUpdateReady(() => setUpdate(() => getPendingUpdate())), []);

  const set = <K extends keyof DiveConditions>(key: K, value: DiveConditions[K]) =>
    setConditions((c) => ({ ...c, [key]: value }));

  // Depth and subject are the two choices that decide the shot, so changing
  // either one re-applies the recommended fps/resolution/aspect for the new
  // combination. Every one of those fields is still a normal chip afterward —
  // overriding one does not stick past the next depth or subject change, and
  // the "Recommended: …" hint below tells you when that has happened.
  const setAndRecommend = <K extends "depthM" | "subject">(key: K, value: DiveConditions[K]) =>
    setConditions((c) => {
      const next = { ...c, [key]: value };
      return { ...next, ...recommend(next) };
    });

  const setFormat = (resolution: Resolution, aspect: AspectRatio) =>
    setConditions((c) => ({ ...c, resolution, aspect }));

  // Switching camera can strand the current format (its chip just vanished
  // from the now-filtered list), so re-recommend only when that happens —
  // unlike depth/subject, an unrelated camera swap should not also reset a
  // frame rate the diver picked on purpose.
  const setCamera = (camera: Camera) =>
    setConditions((c) => {
      const token = RESOLUTION_TOKENS[c.resolution][c.aspect];
      const stillFits = !token?.feature || supports(camera, token.feature);
      // "Tied to this preset" only exists on cameras that support $PRES —
      // stranded the same way an unsupported format would be, so fall back
      // rather than silently keep building a mode this camera can't use.
      const persistenceMode =
        c.persistenceMode === "dial" && !supports(camera, "PRES") ? "session" : c.persistenceMode;
      if (stillFits) return { ...c, camera, persistenceMode };
      const { resolution, aspect } = recommend({ ...c, camera });
      return { ...c, camera, resolution, aspect, persistenceMode };
    });

  // Switching into dial mode re-cleans an existing free-text preset name into
  // a macro key, the same way typing into the field already does — otherwise
  // the field would keep showing e.g. "Reef 14 m" while compileAdvanced
  // silently derives "REEF" from it behind the scenes.
  const setPersistenceMode = (mode: PersistenceMode) =>
    setConditions((c) =>
      mode === "dial"
        ? { ...c, persistenceMode: mode, presetName: cleanMacroName(c.presetName ?? "") }
        : { ...c, persistenceMode: mode },
    );

  const rec = useMemo(() => recommend(conditions), [conditions]);
  const formatOptions = useMemo(() => formatOptionsFor(conditions.camera), [conditions.camera]);
  const advancedMode = conditions.persistenceMode === "dial";

  const { result, advice } = useMemo(
    () => ({
      result: compileConditions(conditions),
      advice: adviseOnConditions(conditions),
    }),
    [conditions],
  );

  return (
    <div className="slate-surface min-h-screen p-5">
      <div className="mx-auto max-w-[1100px]">
        <header className="border-b-2 border-graphite pb-2.5">
          <h1 className="text-[34px] font-bold uppercase leading-[0.95]">Dive slate</h1>
          <p className="mt-0.5 max-w-[60ch] text-[13px] text-graphite-2">
            Build a GoPro Labs QR code for the water you are actually getting into. The command
            updates as you go — everything below is a starting point you can override.
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <OfflineBadge />
            <ThemeToggle />
          </div>
        </header>

        <div className="mt-5 grid gap-5 lg:grid-cols-[1fr_380px] lg:items-start">
          {/* The live result: first on a phone, pinned alongside the settings on a wider screen. */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-5">
            <section className="rounded-[2px] border-2 border-graphite bg-paper p-4">
              <div className="grid gap-[18px] sm:grid-cols-[150px_1fr] lg:grid-cols-1 lg:gap-4">
                <div>
                  <QrCode
                    payload={result.payload}
                    className="border border-slate-line bg-field p-2 [&_svg]:block [&_svg]:h-auto [&_svg]:w-full"
                  />
                  <p className="mt-[7px] text-[11px] leading-tight text-graphite-2">
                    Print or display at 40 mm across or larger. Hold the camera about 150 mm away
                    until the LED blinks twice.
                  </p>
                </div>

                <div>
                  <PayloadPanel
                    result={result}
                    canCapture={!shelf.full}
                    onCapture={() => shelf.add(conditions)}
                  />
                </div>
              </div>
              <Diagnostics items={[...result.diagnostics, ...advice]} />
            </section>
          </div>

          <div className="order-2 grid content-start gap-4 lg:order-1">
            <Section index="01" title="Rig & depth">
              <ChipGroup
                label="Camera"
                value={conditions.camera}
                options={SELECTABLE_CAMERAS.map((c) => ({ value: c, label: CAMERA_LABELS[c] }))}
                onChange={setCamera}
              />
              {/* Only offered where it works. $PRES is HERO12/13 and MAX2, and
                  a field that exists only to be typed into and then dropped is
                  worse than no field — same reasoning as the format chips. */}
              {supports(conditions.camera, "PRES") && (
                <TextField
                  label={advancedMode ? "Dial code" : "Name on the camera dial"}
                  value={conditions.presetName ?? ""}
                  onChange={(v) => set("presetName", advancedMode ? cleanMacroName(v) : v)}
                  placeholder={advancedMode ? "e.g. REEF" : "Optional — e.g. Reef 14 m"}
                  maxLength={advancedMode ? MAX_MACRO_NAME : MAX_PRESET_NAME}
                  // A quote would close the $PRES="…" string early, so the
                  // engine strips it. Stripping a character someone typed
                  // without saying so is the kind of silence this app avoids.
                  // Moot in advanced mode: cleanMacroName already filters as
                  // you type, so there is nothing left to silently strip.
                  note={
                    !advancedMode && /["“”]/.test(conditions.presetName ?? "")
                      ? "Quotes removed"
                      : undefined
                  }
                  hint={
                    advancedMode
                      ? "This name doubles as a macro key, so GoPro can tell which " +
                        "settings belong to this preset — 4 letters, no spaces, and " +
                        "it's what shows on the dial too."
                      : "Saves this as a preset you can pick from the camera itself, " +
                        "with this icon next to it. It stays on the camera until you " +
                        "delete it there."
                  }
                  leading={
                    <button
                      type="button"
                      onClick={() => setIconPickerOpen(true)}
                      title="Change the dial icon"
                      aria-label="Change the dial icon"
                      className="flex shrink-0 cursor-pointer items-center justify-center rounded-[2px] border-[1.5px] border-graphite bg-transparent px-2 text-graphite hover:bg-graphite/10"
                    >
                      <PresetIconGlyph
                        icon={conditions.presetIcon ?? DEFAULT_PRESET_ICON}
                        className="h-5 w-5"
                      />
                    </button>
                  }
                />
              )}

              <ChipGroup
                label="Persistence"
                value={conditions.persistenceMode}
                options={options(
                  supports(conditions.camera, "PRES")
                    ? PERSISTENCE_MODES
                    : PERSISTENCE_MODES.filter((m) => m !== "dial"),
                  PERSISTENCE_MODE_LABELS,
                )}
                onChange={setPersistenceMode}
                hint={
                  advancedMode
                    ? "Wraps the color and exposure fine-tuning in a macro tied to " +
                      "this preset's name, so it actually follows dial selection " +
                      "instead of freezing at whatever was last scanned. Still one " +
                      "code to scan — costs a 4-letter dial code instead of a " +
                      "friendly name."
                    : "Only affects the color and exposure fine-tuning behind this preset — " +
                      "WB lock, red/blue push, damping, stabilisation tuning, bitrate. Until " +
                      "power off resets those at the next boot; Permanent keeps them running " +
                      "past it, until you turn it off here or reset it on the camera. " +
                      "Resolution, format and frame rate stick either way, like any camera " +
                      "setting — and a named preset saves everything above with it regardless " +
                      "of this choice."
                }
              />

              <div className="grid grid-cols-[92px_1fr] items-center gap-4 sm:block">
                <WaterColumn
                  depthM={conditions.depthM}
                  water={conditions.water}
                  onChange={(d) => setAndRecommend("depthM", d)}
                />
                <Spectrum depthM={conditions.depthM} water={conditions.water} />
              </div>
            </Section>

            <Section index="02" title="Water">
              <ChipGroup
                label="Water"
                value={conditions.water}
                options={options(WATER_TYPES, WATER_LABELS)}
                onChange={(v) => set("water", v)}
              />
              <ChipGroup
                label="Visibility"
                value={conditions.visibility}
                options={options(VISIBILITIES, VISIBILITY_LABELS)}
                onChange={(v) => set("visibility", v)}
              />
              <ChipGroup
                label="Light"
                value={conditions.light}
                options={options(LIGHT_SOURCES, LIGHT_LABELS)}
                onChange={(v) => set("light", v)}
              />
            </Section>

            <Section index="03" title="Shot">
              <ChipGroup
                label="Subject"
                value={conditions.subject}
                options={options(SUBJECTS, SUBJECT_LABELS)}
                onChange={(v) => setAndRecommend("subject", v)}
              />
              <ChipGroup
                label="Afterwards"
                value={conditions.workflow}
                options={options(WORKFLOWS, WORKFLOW_LABELS)}
                onChange={(v) => set("workflow", v)}
              />
              <ChipGroup
                label="Exposure comp"
                value={conditions.evComp}
                options={options(EV_COMP_VALUES, EV_COMP_LABELS)}
                onChange={(v) => set("evComp", v)}
                hint="GoPro's own metering tends to overexpose underwater — bright sand, the surface, or a diver's fins fool it into blowing out highlights. -1/2 EV covers most shots; go to -1 EV in especially bright, shallow water."
              />
            </Section>

            <Section
              index="04"
              title="Format"
              note={`Recommended for ${SUBJECT_LABELS[conditions.subject].toLowerCase()} at ${conditions.depthM} m`}
            >
              <ChipGroup
                label="Frame rate"
                value={conditions.frameRate}
                options={FRAME_RATES.map((f) => ({ value: f, label: String(f) }))}
                onChange={(v: FrameRate) => set("frameRate", v)}
                recommendedValue={rec.frameRate}
              />
              <ChipGroup
                label="Resolution & aspect"
                value={formatKey(conditions.resolution, conditions.aspect)}
                options={formatOptions}
                onChange={(v: string) => {
                  const opt = formatOptions.find((o) => o.value === v);
                  if (opt) setFormat(opt.resolution, opt.aspect);
                }}
                recommendedValue={formatKey(rec.resolution, rec.aspect)}
              />
            </Section>
          </div>
        </div>

        <section className="mt-5 rounded-[2px] border-2 border-graphite bg-paper p-4">
          <WhyList tokens={result.emitted} omitted={result.omitted} />
        </section>

        <Shelf
          slots={shelf.slots}
          onRename={shelf.rename}
          onRemove={shelf.remove}
          onMove={shelf.move}
          onLoad={(id) => {
            const found = shelf.conditionsOf(id);
            if (found) setConditions(found);
          }}
          onExport={() => setExporting(true)}
        />

        <SlateDialog slots={shelf.slots} open={exporting} onClose={() => setExporting(false)} />
        <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
        <IconPickerDialog
          open={iconPickerOpen}
          value={conditions.presetIcon ?? DEFAULT_PRESET_ICON}
          onChange={(v) => set("presetIcon", v)}
          onClose={() => setIconPickerOpen(false)}
        />

        {update && <UpdateBanner onApply={update} onDismiss={() => setUpdate(null)} />}

        <footer className="mt-6 max-w-[76ch] text-[11.5px] leading-relaxed text-graphite-2">
          Values here are physically motivated defaults, not measured calibrations, and Labs
          firmware changes. Verify anything new against{" "}
          <a
            className="text-deep underline"
            href="https://gopro.github.io/labs/control/custom/"
            target="_blank"
            rel="noreferrer"
          >
            GoPro's own generator
          </a>{" "}
          before you trust it on a dive that matters.
          <br />
          Found a bug, or a camera we got wrong?{" "}
          <button
            type="button"
            onClick={() => setFeedbackOpen(true)}
            className="cursor-pointer border-0 bg-transparent p-0 font-body text-[11.5px] text-deep underline"
          >
            Send feedback
          </button>
          .
          <br />© 2026 Vasil Vangelovski. All rights reserved. — v{__APP_VERSION__} (
          {__APP_COMMIT__})
        </footer>
      </div>
    </div>
  );
}
